/**
 * Builds an IllustrationRequestPayload from structured inputs.
 * This utility can be used by both client-side (React hooks) and server-side services
 * to construct the payload for illustration request generation.
 */
export function buildIllustrationRequestPayload({
  quote,
  client,
  clientPartner,
  advisor,
  selectedPremium,
  fundingSourceId,
  clientPolicyApplicationId,
}: {
  quote: PolicyQuote | TraditionalLTCQuote;
  client: Client;
  clientPartner?: Client | null;
  advisor?: Advisor;
  selectedPremium?: number;
  fundingSourceId?: string;
  clientPolicyApplicationId?: string;
}): IllustrationRequestPayload {
  // Validate required client data
  if (!client?.intakeSurvey?.clientZipCode) {
    throw new Error('Client zipcode is not available');
  }

  // Determine quote type by checking for traditionalLTCQuoteId property
  const isTraditionalLTC = 'traditionalLTCQuoteId' in quote;
  const quoteId = isTraditionalLTC
    ? (quote as TraditionalLTCQuote).traditionalLTCQuoteId
    : (quote as PolicyQuote).insuranceQuoteId;

  // Build client data based on quote type
  const clientData = isTraditionalLTC
    ? {
        name: `${client.clientFirstName || ''} ${client.clientLastName || ''}`.trim(),
        age: (quote as TraditionalLTCQuote).primaryAge,
        gender: (quote as TraditionalLTCQuote).primaryGender,
      }
    : {
        name: `${client.clientFirstName || ''} ${client.clientLastName || ''}`.trim(),
        age: client.intakeSurvey?.clientAge || 0,
        gender: client.intakeSurvey?.clientGenderAtBirth || '',
      };

  // Build partner data (only for non-traditional LTC quotes)
  const partnerData =
    !isTraditionalLTC && clientPartner
      ? {
          name: `${clientPartner.clientFirstName || ''} ${clientPartner.clientLastName || ''}`.trim(),
          age: clientPartner.intakeSurvey?.clientAge || 0,
          gender: clientPartner.intakeSurvey?.clientGenderAtBirth || '',
        }
      : undefined;

  // Build advisor data
  const advisorData = advisor
    ? {
        advisorFirstName: advisor.advisorFirstName,
        advisorLastName: advisor.advisorLastName,
        organizationDisplayName: advisor.organizationDisplayName,
        organizationName: advisor.organizationName,
        advisorPhoneNumber: advisor.advisorPhoneNumber,
      }
    : undefined;

  // Determine premium amount
  const currentPremium = isTraditionalLTC
    ? (quote as TraditionalLTCQuote).primaryModalPremium
    : selectedPremium || (quote as PolicyQuote).premiumAmount;

  return {
    insuranceQuoteId: quoteId,
    zipcode: client.intakeSurvey.clientZipCode,
    clientId: client.clientId,
    client: clientData,
    partner: partnerData,
    advisor: advisorData,
    premiumAmount: currentPremium,
    quoteType: isTraditionalLTC ? 'traditionalLTC' : 'insurance',
    fundingSourceId,
    clientPolicyApplicationId,
  };
}

